import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Eye, ShieldCheck, Star, Moon, Monitor, Sun, Gem, Palette, Camera as CameraIcon, Mic, Battery } from 'lucide-react';
import { publicApi, useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import ProductBadge from '../components/ProductBadge';
import useSEO from '../hooks/useSEO';
import useSEOPage from '../hooks/useSEOPage';

export default function Collection() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const { categorySlug } = useParams();
  const [sortBy, setSortBy] = useState("newest");
  const [searchQuery, setSearchQuery] = useState("");
  const { user, isPro } = useAuth();
  const { t, tc } = useLanguage();

  // Load categories from API
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await publicApi.get('/categories');
        setCategories([{ name: t('collection.all'), slug: null, icon: '🔍' }, ...data]);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trouver la catégorie active
  const activeCategory = categorySlug 
    ? categories.find(c => c.slug === categorySlug) 
    : categories[0];

  const { seoData } = useSEOPage(categorySlug ? `collection_${categorySlug}` : 'collection');
  
  // Apply SEO meta tags
  useSEO({
    title: seoData?.meta_title,
    description: seoData?.meta_description,
    ogImage: seoData?.og_image,
    url: `https://joeycyan.com/collection${categorySlug ? `/${categorySlug}` : ''}`
  });

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const params = categorySlug ? { category: categorySlug } : {};
        const { data } = await publicApi.get('/products', { params });
        setProducts(data);
      } catch (err) {
        console.error('Error fetching products:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [categorySlug]);

  // Filtrage et tri côté client (recherche et tri seulement, catégorie déjà filtrée côté serveur)
  const filtered = products
    .filter(p => {
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      return (p.name || "").toLowerCase().includes(q) || 
             (p.subtitle || "").toLowerCase().includes(q) || 
             (p.description || "").toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (sortBy === "price_asc") return a.price - b.price;
      if (sortBy === "price_desc") return b.price - a.price;
      if (sortBy === "rating") return (b.rating || 0) - (a.rating || 0);
      return 0;
    });

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="collection-page">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-10">
        {/* Header */}
        <div className="mb-6 md:mb-10">
          <p className="text-xs text-[#00aaff] uppercase tracking-widest font-semibold mb-2">{t('collection.catalog')}</p>
          <h1 className="text-3xl md:text-6xl font-black text-gray-900">{t('collection.title')}</h1>
        </div>

        {/* Pro Banner */}
        {isPro && (
          <div className="mb-6 bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 rounded-2xl p-5 shadow-lg">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 backdrop-blur flex items-center justify-center shrink-0">
                <Star size={24} className="text-white" fill="white" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-bold text-lg mb-1">Tarifs Professionnels Activés ⭐</h3>
                <p className="text-white/90 text-sm leading-relaxed">
                  Vous êtes connecté avec un compte <strong>Professionnel</strong>. Profitez de tarifs préférentiels sur tous nos produits !
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Catégorie active banner */}
        {activeCategory && categorySlug === 'optique' && (
          <div
            className="w-full rounded-2xl mb-6 overflow-hidden shadow-sm"
            style={{ background: 'linear-gradient(90deg, #6E3BFF 0%, #2563EB 45%, #1E9EFF 100%)' }}
            data-testid="category-banner-optique"
          >
            <div className="px-4 md:px-6 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-2 text-xs text-white font-bold uppercase tracking-widest">
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  BLUECUT
                </span>
                <span className="text-white/50">·</span>
                <span className="text-sm text-white font-semibold">
                  Protégez vos yeux de la lumière bleue nocive
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20">
                  <Eye size={14} />
                  Filtre lumière bleue
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20">
                  <Moon size={14} />
                  Améliore le sommeil
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20">
                  <Monitor size={14} />
                  Écrans
                </span>
              </div>
            </div>
          </div>
        )}

        {activeCategory && categorySlug === 'solaire' && (
          <div
            className="w-full rounded-2xl mb-6 overflow-hidden shadow-sm"
            style={{ background: 'linear-gradient(90deg, #FF6A00 0%, #FF8A00 50%, #FFB300 100%)' }}
            data-testid="category-banner-solaire"
          >
            <div className="px-4 md:px-6 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-2 text-xs text-white font-bold uppercase tracking-widest">
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  SOLAIRES
                </span>
                <span className="text-white/60">·</span>
                <span className="text-sm text-white font-semibold">
                  Verres solaires haute protection UV400
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20">
                  <Sun size={14} />
                  UV400
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20">
                  <Gem size={14} />
                  Premium
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20">
                  <Palette size={14} />
                  Style
                </span>
              </div>
            </div>
          </div>
        )}

        {activeCategory && categorySlug === 'camera' && (
          <div
            className="w-full rounded-2xl mb-6 overflow-hidden shadow-sm"
            style={{ background: 'linear-gradient(90deg, #FF1FA3 0%, #C026D3 50%, #7C3AED 100%)' }}
            data-testid="category-banner-camera"
          >
            <div className="px-4 md:px-6 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-2 text-xs text-white font-bold uppercase tracking-widest">
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  CAMERA
                </span>
                <span className="text-white/60">·</span>
                <span className="text-sm text-white font-semibold">
                  Captez chaque moment sans sortir votre téléphone
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20">
                  <CameraIcon size={14} />
                  Photo & Vidéo HD
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20">
                  <Mic size={14} />
                  Micro
                </span>
                <span className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur text-white text-xs font-semibold px-3 py-1.5 rounded-full border border-white/20">
                  <Battery size={14} />
                  Autonomie
                </span>
              </div>
            </div>
          </div>
        )}

        {activeCategory && activeCategory.description && !['optique', 'solaire', 'camera'].includes(categorySlug) && (
          <div className="w-full bg-gradient-to-r from-[#00aaff] via-[#0077ff] to-[#00ddcc] rounded-2xl mb-6 overflow-hidden">
            <div className="px-4 md:px-6 py-3 flex items-center gap-3">
              {activeCategory.icon && <span className="text-xl">{activeCategory.icon}</span>}
              <span className="text-xs text-white font-bold uppercase tracking-widest">{tc(activeCategory.name)}</span>
              <span className="text-white/40">·</span>
              <span className="text-sm text-white font-semibold">{activeCategory.description}</span>
            </div>
          </div>
        )}

        {/* Filters bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6 md:mb-8">
          <div className="flex gap-2 flex-wrap">
            {categories.map(cat => {
              const isActive = cat.slug === categorySlug || (!cat.slug && !categorySlug);
              const href = cat.slug ? `/collection/${cat.slug}` : '/collection';
              
              return (
                <Link
                  key={cat.slug || 'all'}
                  to={href}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all inline-flex items-center gap-1.5 ${
                    isActive
                      ? "bg-[#00aaff] text-white"
                      : "bg-white border border-gray-200 text-gray-500 hover:border-[#00aaff]/40 hover:text-[#00aaff]"
                  }`}
                >
                  {cat.icon && <span>{cat.icon}</span>}
                  {tc(cat.name)}
                </Link>
              );
            })}
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder={t('collection.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="border border-gray-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-[#00aaff]/50 w-40"
            />
            <select 
              value={sortBy} 
              onChange={e => setSortBy(e.target.value)}
              className="bg-white border border-gray-200 text-gray-500 text-sm rounded-xl px-4 py-2 outline-none focus:border-[#00aaff]/50"
            >
              <option value="newest">{t('collection.sortNew')}</option>
              <option value="price_asc">{t('collection.sortPriceAsc')}</option>
              <option value="price_desc">{t('collection.sortPriceDesc')}</option>
              <option value="rating">{t('collection.sortNameAsc')}</option>
            </select>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-gray-100 rounded-2xl h-80 animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24">
            <Eye size={48} className="mx-auto mb-4 text-gray-300" />
            <p className="text-gray-500">{t('collection.noProducts')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-6">
            {filtered.map(p => {
              // Extract original product ID if this is a color variant
              const productId = p.is_color_variant ? p.id.split('__color__')[0] : p.id;
              
              // Build URL with color parameter if it's a color variant
              const productUrl = p.is_color_variant && p.selected_color
                ? `/produit/${p.slug || productId}?color=${p.selected_color.slug || p.selected_color.name}`
                : `/produit/${p.slug || productId}`;
              
              return (
              <Link 
                key={p.id} 
                to={productUrl}
                data-testid={`product-card-${p.id}`}
              >
                <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden hover:border-[#00aaff]/40 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                  <div className="relative h-36 md:h-52 overflow-hidden bg-[#f0f4f8]">
                    {p.images && p.images.length > 0 ? (
                      <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-300">
                        <Eye size={48} />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
                      {p.is_new && <span className="bg-[#00aaff] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">NEW</span>}
                      {p.is_bestseller && <span className="bg-gray-900/70 backdrop-blur text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">BESTSELLER</span>}
                      {isPro && p.pro_price && p.pro_price < p.price && (
                        <span className="bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md">
                          -{Math.round(((p.price - p.pro_price) / p.price) * 100)}% PRO
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="p-3 md:p-5">
                    <p className="font-bold text-gray-900 text-sm md:text-base">{p.name}</p>
                    {p.subtitle && <p className="text-xs text-gray-400 mt-0.5 hidden md:block">{tc(p.subtitle)}</p>}
                    {p.blue_light_filter && (
                      <div className="flex items-center gap-1 mt-2 bg-violet-50 border border-violet-200 rounded-lg px-2 py-1">
                        <ShieldCheck size={11} className="text-violet-500 shrink-0" />
                        <span className="text-[10px] text-violet-600 font-medium leading-tight">Filtre lumière bleue intégré</span>
                      </div>
                    )}
                    <ProductBadge badge={p.custom_badge} size="sm" />
                    <div className="flex justify-between items-center mt-2 md:mt-4">
                      <div className="flex flex-col gap-1">
                        {isPro && p.pro_price && p.pro_price < p.price ? (
                          <>
                            <span className="text-base md:text-xl font-black text-amber-600">{p.pro_price}€</span>
                            <span className="text-xs text-gray-400 line-through">{p.price}€</span>
                          </>
                        ) : (
                          <span className="text-base md:text-xl font-black text-[#00aaff]">{p.price}€</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
